import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import FeaturedVideoIcon from "@material-ui/icons/FeaturedVideo";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import RefreshIcon from "@material-ui/icons/Refresh";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { endPoint, graphqlUrl } from "utils/config";
import "../superAds.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * There is no getAdvertisementsCount on the backend, so this list is
 * fetched whole and paged in the browser. Advertisements are a handful
 * of banners, not an unbounded table, so that is cheaper than adding a
 * resolver — and it lets the live/scheduled/expired split, which is
 * derived from the dates rather than stored, be filtered honestly.
 * ------------------------------------------------------------------ */

const GET_ADVERTISEMENTS = gql`
  query getAdvertisements {
    getAdvertisements {
      _id
      title
      description
      redirectTo
      photoUrl
      startAt
      endAt
      createdAt
      updatedAt
    }
  }
`;

const REMOVE_ADVERTISEMENTS = gql`
  mutation removeAdvertisementByAdmin($idSet: [ID!]!) {
    removeAdvertisementByAdmin(idSet: $idSet) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const LIFECYCLE_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "live", label: "Running now" },
  { value: "scheduled", label: "Scheduled" },
  { value: "expired", label: "Finished" },
];
const LIFECYCLE_LABELS = LIFECYCLE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "title", label: "Title A-Z" },
];

const FILTER_DEBOUNCE_MS = 250;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : null);

const assetUrl = (path) =>
  !path ? null : /^https?:\/\//i.test(path) ? path : `${graphqlUrl}${path.startsWith("/") ? "" : "/"}${path}`;

const lifecycleOf = (ad) => {
  const now = moment();
  if (ad.endAt && moment(ad.endAt).isBefore(now)) {
    return { key: "expired", tone: "neutral", label: "Finished" };
  }
  if (ad.startAt && moment(ad.startAt).isAfter(now)) {
    return { key: "scheduled", tone: "info", label: "Scheduled" };
  }
  return { key: "live", tone: "success", label: "Running" };
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Advertisements = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_ADVERTISEMENTS, {
    fetchPolicy: "network-only",
  });
  const [removeAdvertisements] = useMutation(REMOVE_ADVERTISEMENTS);

  useEffect(() => {
    const handler = setTimeout(() => {
      setAppliedSearch(searchTerm.trim().toLowerCase());
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPageCount(0);
    setSelectedIds([]);
  }, [lifecycleFilter, sortOption, perPage]);

  const allAds = (data && data.getAdvertisements) || [];

  const filtered = allAds
    .filter((ad) => {
      if (lifecycleFilter && lifecycleOf(ad).key !== lifecycleFilter) return false;
      if (!appliedSearch) return true;
      return (
        (ad.title || "").toLowerCase().indexOf(appliedSearch) !== -1 ||
        (ad.description || "").toLowerCase().indexOf(appliedSearch) !== -1
      );
    })
    .sort((a, b) => {
      if (sortOption === "title") return (a.title || "").localeCompare(b.title || "");
      const left = new Date(a.createdAt || 0).getTime();
      const right = new Date(b.createdAt || 0).getTime();
      return sortOption === "oldest" ? left - right : right - left;
    });

  const ads = filtered.slice(pageCount * perPage, pageCount * perPage + perPage);

  const liveCount = allAds.filter((ad) => lifecycleOf(ad).key === "live").length;
  const scheduledCount = allAds.filter((ad) => lifecycleOf(ad).key === "scheduled").length;
  const expiredCount = allAds.filter((ad) => lifecycleOf(ad).key === "expired").length;

  /* ---------------- selection + delete ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = ads.map((ad) => ad._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    ads.length > 0 && selectedIds.length === ads.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  const runDelete = () => {
    const targets = deleteTarget || [];
    setDeleting(true);
    setActionError("");
    removeAdvertisements({ variables: { idSet: targets.map((ad) => ad._id) } })
      .then(() => {
        setDeleting(false);
        setDeleteTarget(null);
        setPreviewTarget(null);
        setSelectedIds([]);
        refetch();
      })
      .catch((err) => {
        setDeleting(false);
        setDeleteTarget(null);
        setActionError(
          (err.graphQLErrors && err.graphQLErrors.length && err.graphQLErrors[0].message) ||
            "Could not delete. Please try again."
        );
      });
  };

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (searchTerm.trim()) activeFilterChips.push({ id: "search", label: `Search: ${searchTerm}` });
  if (lifecycleFilter) {
    activeFilterChips.push({ id: "lifecycle", label: LIFECYCLE_LABELS[lifecycleFilter] });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "lifecycle") setLifecycleFilter("");
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setLifecycleFilter("");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superAdsPage">
        <main className="main">
          <PageHeader title="Advertisements" description="Banners shown to customers in the app." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={5} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedAds = ads.filter((ad) => selectedIds.includes(ad._id));

  return (
    <div className="page superAdsPage">
      <main className="main">
        <PageHeader
          title="Advertisements"
          count={allAds.length}
          description="Banners shown to customers in the app, and when each one runs."
          actions={
            <Button
              variant="primary"
              onClick={() => (window.location.href = `${endPoint}/superAdmin/addAdvertisement`)}
            >
              New advertisement
            </Button>
          }
        />

        <section className="stats">
          <MetricCard icon={<FeaturedVideoIcon />} title="All banners" value={allAds.length} />
          <MetricCard
            icon={<PlayCircleOutlineIcon />}
            title="Running now"
            value={liveCount}
            tone={liveCount > 0 ? "success" : "default"}
          />
          <MetricCard icon={<ScheduleIcon />} title="Scheduled" value={scheduledCount} />
          <MetricCard icon={<EventBusyIcon />} title="Finished" value={expiredCount} />
        </section>

        <section className="filters">
          <SearchField
            placeholder="Search by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={lifecycleFilter ? LIFECYCLE_LABELS[lifecycleFilter] : "Status"}
              options={LIFECYCLE_OPTIONS}
              value={lifecycleFilter}
              onChange={(e) => setLifecycleFilter(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteTarget(selectedAds)}
            >
              Delete selected
            </Button>
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh advertisements"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superAdsSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every banner on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Banner</div>
              <div className="tableHeaderCell">Links to</div>
              <div className="tableHeaderCell">Runs</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {ads.length === 0 ? (
              <EmptyState
                icon={<FeaturedVideoIcon />}
                title={activeFilterChips.length === 0 ? "No advertisements yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Create a banner to promote something in the customer app."
                    : "No banners match these filters. Try widening or clearing them."
                }
                action={
                  activeFilterChips.length === 0 ? (
                    <Button
                      variant="primary"
                      onClick={() =>
                        (window.location.href = `${endPoint}/superAdmin/addAdvertisement`)
                      }
                    >
                      New advertisement
                    </Button>
                  ) : null
                }
              />
            ) : (
              ads.map((ad) => {
                const lifecycle = lifecycleOf(ad);
                const photo = assetUrl(ad.photoUrl);
                return (
                  <div className="row" key={ad._id}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(ad._id)}
                      onChange={() => toggleSelectOne(ad._id)}
                      inputProps={{ "aria-label": `Select ${ad.title}` }}
                    />

                    <div className="superAdCell" data-label="Banner">
                      <button
                        type="button"
                        className="superAdThumb"
                        onClick={() => setPreviewTarget(ad)}
                        title="Preview banner"
                        style={{ padding: 0, cursor: "pointer" }}
                      >
                        {photo ? <img src={photo} alt="" /> : <ImageIcon fontSize="small" />}
                      </button>
                      <span className="superAdText">
                        <span className="superAdTitle">{ad.title}</span>
                        {ad.description && (
                          <span className="superAdDescription">{ad.description}</span>
                        )}
                      </span>
                    </div>

                    <span data-label="Links to">
                      {ad.redirectTo ? (
                        <a
                          className="superAdLink"
                          href={ad.redirectTo}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {ad.redirectTo}
                          <OpenInNewIcon style={{ fontSize: 14 }} />
                        </a>
                      ) : (
                        <span className="superAdMuted">No link</span>
                      )}
                    </span>

                    <span className="updated" data-label="Runs">
                      <strong>{formatDate(ad.startAt) || "No start"}</strong>
                      <span>{formatDate(ad.endAt) ? `until ${formatDate(ad.endAt)}` : "No end date"}</span>
                    </span>

                    <span data-label="Status">
                      <StatusBadge status={lifecycle.tone} label={lifecycle.label} />
                    </span>

                    <div className="superAdActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() =>
                          (window.location.href = `${endPoint}/superAdmin/editAdvertisement/${ad._id}`)
                        }
                        title="Edit advertisement"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDeleteTarget([ad])}
                        title="Delete advertisement"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {filtered.length > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={filtered.length}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(previewTarget)}
        onClose={() => setPreviewTarget(null)}
        title="Banner preview"
        subtitle={previewTarget ? previewTarget.title : ""}
        size="lg"
      >
        {previewTarget && (
          <div className="superAdPreview">
            {assetUrl(previewTarget.photoUrl) ? (
              <img
                className="superAdPreviewImage"
                src={assetUrl(previewTarget.photoUrl)}
                alt={previewTarget.title}
              />
            ) : (
              <span className="superAdMuted">This advertisement has no image.</span>
            )}
            {previewTarget.description && <p>{previewTarget.description}</p>}
            <div className="superAdFooter">
              <Button
                variant="secondary"
                onClick={() =>
                  (window.location.href = `${endPoint}/superAdmin/editAdvertisement/${previewTarget._id}`)
                }
              >
                Edit
              </Button>
            </div>
          </div>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title={deleteTarget && deleteTarget.length > 1 ? "Delete advertisements" : "Delete advertisement"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: deleting },
          { label: "Delete", onClick: runDelete, variant: "danger", loading: deleting },
        ]}
      >
        <div className="superAdConfirm">
          {deleteTarget && deleteTarget.length > 1
            ? `${deleteTarget.length} banners will stop being shown to customers.`
            : "This banner will stop being shown to customers."}
        </div>
      </DialogShell>
    </div>
  );
};

export default Advertisements;
